import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, Eye, EyeOff, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const ADMIN_TOKEN_KEY = "recoverymatch_admin_token";
const ADMIN_USER_KEY = "recoverymatch_admin_user";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [cleared, setCleared] = useState(false);

  const handleClearSession = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("recoverymatch_token");
    localStorage.removeItem("recoverymatch_user");
    localStorage.removeItem("recoverymatch_admin_token");
    localStorage.removeItem("recoverymatch_admin_user");
    setCleared(true);
    setError("");
    setTimeout(() => {
      setCleared(false);
      window.location.reload();
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Use separate admin auth endpoint
      const response = await api.post("/admin/auth/login", {
        email: email.toLowerCase(),
        password
      });

      if (response.data.token && response.data.admin) {
        // Clear any regular user tokens first
        localStorage.removeItem("token");
        localStorage.removeItem("recoverymatch_token");
        localStorage.removeItem("recoverymatch_user");
        
        // Store admin token separately from user token
        localStorage.setItem(ADMIN_TOKEN_KEY, response.data.token);
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(response.data.admin));
        
        // Force page reload to ensure clean state
        window.location.href = "/admin";
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      let message = "Invalid credentials";
      
      // Check for timeout or network errors
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        message = "Connection timeout - please try again";
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        message = "Unable to connect to server - please try again";
      } else if (err.response?.status === 500) {
        message = "Server error - please try again in a moment";
      } else if (err.response?.data?.detail) {
        message = err.response.data.detail;
      }
      
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1C3A7A] to-[#0f2347] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FAB710] rounded-full mb-4">
            <Shield className="w-8 h-8 text-[#1C3A7A]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Portal</h1>
          <p className="text-white/60">RecoveryMatch Administration</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent outline-none transition"
                placeholder="admin@recoverymatch.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent outline-none transition pr-12"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1C3A7A] hover:bg-[#15305f] text-white py-3 rounded-lg font-semibold transition"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in...
                </span>
              ) : (
                "Sign In to Admin"
              )}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center space-y-3">
            <button
              type="button"
              onClick={handleClearSession}
              className="text-sm text-red-500 hover:text-red-700 transition"
            >
              {cleared ? "✓ Session Cleared!" : "Clear Browser Session"}
            </button>
            <div>
              <a
                href="/"
                className="text-sm text-gray-500 hover:text-[#1C3A7A] transition"
              >
                ← Back to RecoveryMatch
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-white/40 text-sm mt-6">
          © 2026 RecoveryMatch. All rights reserved.
        </p>
      </div>
    </div>
  );
};

// Helper functions for admin auth
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);
export const getAdminUser = () => {
  const user = localStorage.getItem(ADMIN_USER_KEY);
  return user ? JSON.parse(user) : null;
};
export const isAdminLoggedIn = () => !!getAdminToken();
export const adminLogout = () => {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_USER_KEY);
};

export default AdminLoginPage;
